import React from 'react';
import PaymentCollectionTaskPanel, {
  PaymentLinkTaskDetails,
} from './PaymentCollectionTaskPanel';

/** Mirrors `FlightPaymentHold` in dream-be (lib/flight/paymentTask.ts). */
export type FlightPaymentHold =
  | {
      kind: 'held_order';
      orderId: string;
      bookingReference: string;
      payUntil: string | null;
    }
  | { kind: 'instant_after_payment'; offerExpiresAt: string };

/** Mirrors `FlightPaymentTaskDetails` in dream-be (lib/flight/paymentTask.ts). */
export interface FlightPaymentTaskDetails extends PaymentLinkTaskDetails {
  kind: 'flight_payment';
  route: string;
  airline: string;
  itinerary: string;
  fareBrand: string | null;
  conditions: string;
  passengerNames: string[];
  hold: FlightPaymentHold;
}

interface FlightPaymentTaskPanelProps {
  taskId: string;
  details: FlightPaymentTaskDetails;
  status: string;
  readOnly?: boolean;
  onClosed?: () => void;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB');
}

function holdNotice(hold: FlightPaymentHold): React.ReactNode {
  if (hold.kind === 'held_order') {
    return (
      <>
        Seats held by the airline under <b>{hold.bookingReference}</b> at this
        price
        {hold.payUntil ? ` until ${formatWhen(hold.payUntil)}` : ''}. Dream pays
        the airline the moment the payment is confirmed; if the hold lapses
        first the seats are released and the user is told.
      </>
    );
  }
  return (
    <>
      <b>Pay-at-booking fare — seats are not held while you collect.</b> Take
      the payment promptly (fare shown was valid until{' '}
      {formatWhen(hold.offerExpiresAt)}). Dream re-checks the fare when the
      payment lands and books only if the price and terms are unchanged;
      otherwise the payment is refunded and the user is told.
    </>
  );
}

/** Ops panel for a `flight_payment` task; see PaymentCollectionTaskPanel. */
const FlightPaymentTaskPanel: React.FC<FlightPaymentTaskPanelProps> = ({
  taskId,
  details,
  status,
  readOnly = false,
  onClosed,
}) => (
  <PaymentCollectionTaskPanel
    taskId={taskId}
    details={details}
    status={status}
    booksWhat='the flight'
    testId='flight-payment-panel'
    readOnly={readOnly}
    onClosed={onClosed}
    notice={holdNotice(details.hold)}
  >
    <div className='detail-row'>
      <span className='detail-label'>Flight:</span>
      <span className='detail-value'>
        {details.route} with {details.airline}
      </span>
    </div>
    <div className='detail-row'>
      <span className='detail-label'>Itinerary:</span>
      <span className='detail-value'>{details.itinerary}</span>
    </div>
    <div className='detail-row'>
      <span className='detail-label'>Fare:</span>
      <span className='detail-value'>
        {details.fareBrand ?? 'Standard'} — {details.conditions}
      </span>
    </div>
    <div className='detail-row'>
      <span className='detail-label'>Passengers:</span>
      <span className='detail-value'>{details.passengerNames.join(', ')}</span>
    </div>
    {details.hold.kind === 'held_order' && (
      <div className='detail-row'>
        <span className='detail-label'>Airline reference:</span>
        <span className='detail-value'>{details.hold.bookingReference}</span>
      </div>
    )}
  </PaymentCollectionTaskPanel>
);

export default FlightPaymentTaskPanel;
